/**
 * @flow
 * @format
 */

import React from 'react';
import * as d3 from 'd3';
import ReactFauxDOM from 'react-faux-dom';
import _ from 'lodash';
import * as utils from '~/utils';

import type { ChartDataType } from '~/modules/design/types';

type LabelsType = {
  'y1.legned'?: string,
  'y2.legend'?: string,
  Detail?: ?string,
  x: string,
  y1: string,
  y2?: string,
};

type DataPointType = { id: string | number, key: string | number, val: number };

type MarkerObjectType = {
  'Total Module Loss'?: number,
  'Y-axis upper range'?: number,
  IOverload?: number,
  Irated?: number,
  MaxTjDh?: number,
  MaxTjigbt?: number,
  MeanTjDh?: number,
  MeanTjigbt?: number,
};

type PropsType = {
  area?: Array<{
    inside: boolean,
    name: string,
    style: CSSRules,
  }>,
  aspectRatio: string,
  data: ChartDataType,
  height: ?number,
  labels: LabelsType,
  markers: MarkerObjectType,
  viewBox: ViewBox | null,
  width: ?number | string,
  legend: ?string,
};

function getTextWidth(
  text: string,
  fontSize: string | number,
  fontFace?: string = '',
) {
  const a = document.createElement('canvas');
  const b = a.getContext('2d');
  b.font = `${fontSize}px ${fontFace}`;
  return b.measureText(text).width;
}

// calculate the width of the text
const calculateTextWidth = (
  keys: string[] | string,
  textSize: number = 12,
): number => {
  const canvas = document.createElement('canvas');

  const ctx = canvas.getContext('2d');

  if (ctx) {
    ctx.font = `${textSize}px Arial`;

    if (typeof keys === 'string') {
      return ctx.measureText(keys).width;
    }

    return keys.reduce((ret: number, str: string): number => {
      const len = ctx.measureText(str).width;
      if (len > ret) return len;
      return ret;
    }, 0);
  }
  return 0;
};

function replaceLegendText(str: string) {
  return `${_.startCase(str[0])}${str.slice(1)}`;
}

const getColor = (key: string) => {
  const color = d3
    .scaleOrdinal(d3.schemeCategory20b)
    .domain([
      'Total FET(s)',
      'Total diode',
      '50% load',
      'Switching',
      'Coss',
      '6',
      'Max Vin',
      'Rg',
      '100% load',
      '3',
      'Conduction',
      '25% load',
      'Nom Vin',
      'Rev. recov.',
      'Min Vin',
      '2',
      '4',
      '75% load',
      'Max Load',
      'Leakage',
    ]);

  if (
    [
      'Hi-Side IGBT/MOSFET Current(amps)',
      'Thermal Resistance Limited by Ths(°C)',
    ].includes(key)
  ) {
    return 'blue';
  }

  if (
    [
      'Hi-Side Modulation Signal',
      'Thermal Resistance Limited by Tj(°C)',
    ].includes(key)
  ) {
    return 'green';
  }

  return color(key);
};

export class MultiLineGraph extends React.PureComponent<PropsType, *> {
  static defaultProps = {
    area: [],
  };

  // componentWillMount() {
  //   const { data: orgData, viewBox, width, height, labels } = this.props;
  //   const data = _.omit(orgData, 'Unit');

  //   // Mouse over effects
  //   const svgWrapper = d3.select('.groupWrapper');
  //   const mouseG = svgWrapper.append('g')
  //     .attr('class', 'mouse-over-effects');

  //   mouseG.append('path') // this is the black vertical line to follow mouse
  //     .attr('class', 'mouse-line')
  //     .style('stroke', 'black')
  //     .style('stroke-width', '1px')
  //     .style('opacity', '0');

  //   const lines = document.getElementsByClassName('line');

  //   console.log(svgWrapper.node(), data);
  //   const mousePerLine = mouseG.selectAll('.mouse-per-line')
  //     .data(data)
  //     .enter()
  //     .append('g')
  //     .attr('class', 'mouse-per-line');

  //   mousePerLine.append('circle')
  //     .attr('r', 7)
  //     .style('stroke', d => getColor(d.id))
  //     .style('fill', 'none')
  //     .style('stroke-width', '1px')
  //     .style('opacity', '0');

  //   mousePerLine.append('text')
  //     .attr('transform', 'translate(10,3)');

  //   /* eslint-disable indent */

  //   mouseG.append('svg:rect') // append a rect to catch mouse movements on canvas
  //     .attr('width', width) // can't catch mouse events on a g element
  //     .attr('height', height)
  //     .attr('fill', 'none')
  //     .attr('pointer-events', 'all')
  //     .on('mouseout', () => { // on mouse out hide line, circles and text
  //       d3.select('.mouse-line')
  //         .style('opacity', '0');
  //       d3.selectAll('.mouse-per-line circle')
  //         .style('opacity', '0');
  //       d3.selectAll('.mouse-per-line text')
  //         .style('opacity', '0');
  //     })
  //     .on('mouseover', () => { // on mouse in show line, circles and text
  //       d3.select('.mouse-line')
  //         .style('opacity', '1');
  //       d3.selectAll('.mouse-per-line circle')
  //         .style('opacity', '1');
  //       d3.selectAll('.mouse-per-line text')
  //         .style('opacity', '1');
  //     })
  //     .on('mousemove', () => { // mouse moving over canvas
  //       const mouse = d3.mouse(this);
  //       console.log('mmove');
  //       d3.select('.mouse-line')
  //         .attr('d', () => `M${mouse[0]},${height} ${mouse[0]}, 0`);
  //       // var d = 'M' + mouse[0] + ',' + height;
  //       // d += ' ' + mouse[0] + ',' + 0;
  //       // return d;
  //     });

  //       // d3.selectAll('.mouse-per-line')
  //       //   .attr('transform', (d, i) => {
  //       //     console.log(width / mouse[0]);
  //       //     const xDate = x.invert(mouse[0]);
  //       //     const bisect = d3.bisector(f => f.date).right;
  //       //     const idx = bisect(d.values, xDate);

  //       //     let beginning = 0;
  //       //     let end = lines[i].getTotalLength();
  //       //     let target = null;
  //       //     let pos = {};

  //       //     while (true) {
  //       //       target = Math.floor((beginning + end) / 2);
  //       //       pos = lines[i].getPointAtLength(target);
  //       //       if ((target === end || target === beginning) && pos.x !== mouse[0]) {
  //       //         break;
  //       //       }
  //       //       if (pos.x > mouse[0]) end = target;
  //       //       else if (pos.x < mouse[0]) beginning = target;
  //       //       else break; // position found
  //       //     }

  //       //     d3.select(this).select('text')
  //       //       .text(y1.invert(pos.y).toFixed(2));

  //       //     return `translate(${mouse[0]},${pos.y})`;
  //       //   });
  //     // });
  // }

  props: PropsType;

  render(): ?React$Element<*> {
    const {
      data: orgData,
      viewBox,
      width,
      height,
      labels,
      aspectRatio,
      legend: legendPos,
    } = this.props;
    let { markers } = this.props;

    const data = _.omit(orgData, 'Unit');
    // const data = _.pick(orgData, 'total FET(s)');
    const xAxisData = orgData.Unit;
    // const y1AxisData = data[labels.y1];
    // const y2AxisData = data[labels.y2];

    // No data? Gitoutta here.
    if (!_.size(data)) return null;

    // Remap the data into a format that is easier to consume with d3
    const thisXData: Array<Array<DataPointType>> = _.map(
      data,
      (values: number[], key: string): Array<*> => {
        let vals = values;
        // this is dumb.. some data is not the same length.
        if (xAxisData.length < values.length) {
          vals = values.slice(0, xAxisData.length);
        }
        return vals.map((value, index) => ({
          id: key,
          key: xAxisData[index],
          val: value,
        }));
      },
    );

    // vs. only vs Load should have %
    const showVin = data['100% load'];

    // Remap the Y data to a format that is easier to consume with d3: [][]
    const thisYData: Array<Array<DataPointType>> = _.map(
      data,
      (values: number[], key: string): Array<*> => {
        const vals = values;
        return vals.map((value, index) => ({
          id: key,
          key: showVin ? xAxisData[index] : index,
          val: value,
        }));
      },
    );

    // Fatten the [][]
    const flatData = _.flatten(thisXData);
    const flatYData = _.flatten(thisYData);

    // Set up the base SVG
    const svg: Element = ReactFauxDOM.createElement('svg');
    const svgWrapper: any = d3
      .select(svg)
      .attr('class', 'graph')
      .attr(
        'viewBox',
        viewBox
          ? `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height + 50}`
          : null,
      )
      .attr('width', width)
      .attr('height', height)
      // .attr('data', null)
      // .append('g');
      .attr('preserveAspectRatio', aspectRatio);

    const el = svgWrapper
      .append('g')
      .attr('class', 'groupWrapper')
      .attr('transform', 'scale(0.9) translate(50)');

    if (!viewBox) {
      el.attr('transform', 'translate(150, 0)');
    }

    // set a container with margins
    const group = el.append('g').attr('transform', 'translate(0, 20)');

    const vbWidth = viewBox ? viewBox.width : 400;
    const vbHeight = viewBox ? viewBox.height : 400;

    let x = d3
      .scalePow()
      .range([0, vbWidth])
      .domain(d3.extent(flatYData, d => d.key))
      .clamp(true)
      .nice();

    let y1 = d3
      .scaleLinear()
      .range([vbHeight, 0])
      .domain(d3.extent(flatYData, d => d.val))
      .clamp(true)
      .nice();

    const yUpper = markers['Y-axis upper range'] || false;
    const xUpper = d3.max(flatData, v => v.key);
    markers = _.omit(markers, 'Y-axis upper range');

    if (yUpper) {
      y1 = d3
        .scaleLinear()
        .range([vbHeight, 0])
        // .domain(d3.extent(flatData, d => d.val))
        .domain([0, yUpper])
        .clamp(true);

      x = d3
        .scaleLinear()
        .range([0, vbWidth])
        .domain(d3.extent(flatData, d => d.key))
        .clamp(true);
    }

    // const y2 = d3.scaleLinear()
    //   .range([viewBox.height, d3.min(flatData, v => v.val)])
    //   // .domain(d3.extent(flatData, d => d.val))
    //   .domain(d3.extent(y2AxisData))
    //   .clamp(true)
    //   .nice();

    const y1Line = d3
      .line()
      .curve(d3.curveMonotoneX)
      .x(d => x(d.key))
      .y(d => y1(d.val));

    // Area
    // let area1 = null;
    // if (this.props.area.length > 0) {
    //   area1 = d3.area()
    //     .x(d => x(d.key))
    //     // .y0(viewBox.height)
    //     .y(d => y1 && y1(d.val));
    // }

    type MarkerType =
      | { lines: Array<any> }
      | Array<{ x: Array<number>, y: Array<number> }>;
    const getMarkerLines = (obj: any): MarkerType => {
      let lines = [];

      if (!obj) return { lines };

      if (obj['min Vin'] && obj['max Vin']) {
        // hack - crit graph
        const horzMarkers = _.omit(obj, ['max load', 'Y-axis upper range']);
        // const vertMarkers = _.pick(obj, 'max load');
        const vertMarkers = {};

        return [
          {
            x: _.map(horzMarkers, h => x(h)),
            y: _.map(vertMarkers, (v: number) => {
              if (y1) {
                return y1(v);
              }
              return -1;
            }),
          },
        ];
      }

      const iOverload = obj['I-Overload'];
      const iRated = obj['I-rated'];
      if (iOverload && iRated) {
        const niceX = x.nice();
        lines = [
          d3
            .line()
            .x(() => niceX(iOverload))
            .y(d => y1 && y1(d.val)),
          d3
            .line()
            .x(() => niceX(iRated))
            .y(d => y1 && y1(d.val)),
        ];
      }

      return {
        lines,
      };
    };

    const markerLines = getMarkerLines(markers);

    // Create axis
    const xAxis = d3.axisBottom(x);
    const yAxis = d3.axisLeft(y1);

    // Create x-axis
    group
      .append('g')
      .attr('class', 'axis')
      .attr('transform', `translate(0, ${vbHeight})`)
      .call(xAxis);

    const textSpan = k => {
      if (!k) return k;
      if (k.includes('Vin')) {
        return (
          <tspan key={k}>
            {k.replace(/in$/, '')}
            <tspan fontSize="7" baselineShift="sub">
              IN
            </tspan>
          </tspan>
        );
      }
      return <tspan key={k}>{k}</tspan>;
    };

    // Add x-axis label
    group
      .append('g')
      .attr('transform', `translate(${vbWidth / 2}, ${vbHeight + 35})`)
      .append('text')
      .attr('class', 'x label')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .attr('text-anchor', 'middle')
      .text(textSpan(utils.toTitleCase(labels.x)));

    // Create y-axis
    group
      .append('g')
      .attr('class', 'axis')
      .call(yAxis);

    // Add y-axis label
    group
      .append('g')
      .attr('transform', `translate(${-40}, ${vbHeight / 2})`)
      .append('text')
      .attr('class', 'y label')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .attr('text-anchor', 'middle')
      .attr('transform', 'rotate(-90)')
      .text(textSpan(utils.toTitleCase(labels.y1)));

    // Add y-axis 2 label if necessary
    group
      .append('g')
      .attr('transform', `translate(${vbWidth + 40}, ${vbHeight / 2})`)
      .append('text')
      .attr('class', 'y label')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .attr('text-anchor', 'middle')
      .attr('transform', 'rotate(90)')
      .text(utils.toTitleCase(labels.y2));

    // Create the grid
    group
      .append('g')
      .attr('class', 'grid')
      .attr('transform', `translate(0, ${vbHeight})`)
      .call(xAxis.tickSize(-vbHeight, 0, 0).tickFormat(''));

    group
      .append('g')
      .attr('class', 'grid')
      .attr('transform', 'translate(0, 0)')
      .call(yAxis.tickSize(-vbWidth, 0, 0).tickFormat(''));

    // Add legend
    const legendRectSize = 11;
    const legendPad = 2;
    const textSize = 12;

    const legend = el
      .append('g')
      .attr('class', 'legend')
      .attr('transform', 'translate(5, 0)');

    // make the legend more box like

    const legendRow = legend.append('g');

    legendRow
      .append('rect')
      .attr('class', 'legendBackground')
      // .style('stroke', 'black')
      // .attr('transform', `translate(${legendPad}, ${legendPad})`)
      .style('stroke-width', 0.5);

    // Append all of our lines and legend text
    let count = 0;
    let runningWidth = 0;
    const theData = yUpper ? thisXData : thisYData;

    const pathGroup = group.append('g');
    _.forEach(theData, (value: Array<DataPointType>) => {
      const legendKey = replaceLegendText(_.get(value[0], ['id'], 'unknown'));
      const row = legendRow.append('g').attr('class', 'legendRow');

      const textLen = getTextWidth(legendKey, textSize);

      // Legend text
      if (legendPos === 'vertical') {
        row
          .append('rect')
          .attr('width', legendRectSize)
          .attr('height', legendRectSize)
          .attr('x', 0)
          .attr('y', count * (legendRectSize + legendPad))
          .style('fill', getColor(legendKey));

        row
          .append('text')
          .attr('x', legendRectSize + legendPad)
          .attr('y', count * (legendRectSize + legendPad))
          .attr('dy', legendRectSize)
          .style('fill', 'black')
          .attr('font-size', textSize)
          .attr('transform', 'translate(0, -2)')
          .text(textSpan(legendKey));
      } else {
        const legendTextSpan = textSpan(legendKey);
        const defSpacing = legendRectSize + legendPad;
        row
          .append('rect')
          .attr('width', legendRectSize)
          .attr('height', legendRectSize)
          .attr('x', runningWidth + count * legendRectSize)
          .attr('y', 0)
          .style('fill', getColor(legendKey));
        row
          .append('text')
          .attr('x', runningWidth + count * defSpacing)
          .attr('dx', defSpacing)

          // spacing after the square
          .attr('y', 0)
          .attr('dy', legendRectSize)
          .style('fill', 'black')
          .attr('font-size', textSize)
          .attr('transform', 'translate(0, 0)')
          .text(legendTextSpan);
        runningWidth += textLen + legendRectSize + legendPad * 4;
      }

      count += 1;

      pathGroup
        .append('path')
        .data([value])
        .attr('class', 'line')
        .attr('d', y1Line)
        .style('stroke', getColor(legendKey));

      if (_.isArray(markerLines)) {
        const xlines = markerLines[0].x;
        const ylines = markerLines[0].y;

        _.forEach(xlines, (lineX: number, index: number) =>
          pathGroup
            .append('line')
            .attr('class', 'line')
            .attr('x1', lineX)
            .attr('y1', y1 && y1(0))
            .attr('x2', lineX)
            .attr('y2', y1 && y1(yUpper))
            .style('stroke', 'black')
            // IE11 doesn't handle '0, 0' for dasharrays.
            .style('stroke-dasharray', [0, 1].includes(index) ? '0' : '3, 5')
            .style('stroke-width', 1),
        );

        // horizontal lines
        _.forEach(ylines, (lineY: number) =>
          pathGroup
            .append('line')
            .attr('class', 'line')
            .attr('x1', x(0))
            .attr('y1', lineY)
            .attr('x2', x(xUpper))
            .attr('y2', lineY)
            .style('stroke', 'blue')
            // .style('stroke-dasharray', '3, 5')
            .style('stroke-width', 1.6),
        );
      } else {
        const lines = _.get(markerLines, 'lines', []);
        _.forEach(lines, line =>
          pathGroup
            .append('path')
            .data([value])
            .attr('class', 'line')
            .attr('d', line)
            .style('stroke', 'black')
            .style('stroke-dasharray', '3, 5')
            .style('stroke-width', 0.85),
        );
      }
    });
    let index = 0;
    _.forEach(markers, (val: number, key: string) => {
      if (key === 'fs') return;

      const anchor = () => {
        if (Object.keys(markers).length === 2) return 'center';
        return key.includes('mode') ? 'start' : 'end';
      };
      group
        .append('text')
        .attr('y', -x(val))
        .attr('x', 0)
        .style('fill', 'black')
        .style('font-size', '14px')
        .attr('text-anchor', anchor)
        .attr(
          'transform',
          `translate(${index % 2 ? '-14' : '8'}, 55) rotate(90)`,
        )
        .text(textSpan(key));
      index += 1;
    });

    // if (this.props.area.length) {
    //   // Append the area
    //   const values = thisXData[0]; // critical load
    //   pathGroup.append('path')
    //     .attr('class', 'area')
    //     .data([values])
    //     .attr('d', area1);
    // }

    // reattach for zindex order
    legend.raise();

    const textWidth = calculateTextWidth(Object.keys(data), textSize);

    if (legendPos === 'vertical') {
      legend
        .select('.legendBackground')
        .attr('height', legendPad + count * (legendRectSize + legendPad))
        .attr('fill', 'white')
        .attr('opacity', '0.65')
        .attr('width', textWidth + 10 + (legendRectSize + legendPad * 3));
    } else {
      legend
        .select('.legendBackground')
        .attr('height', legendPad * 2 + legendRectSize)
        .attr('fill', 'white')
        .attr('opacity', '0.65')
        .attr('width', runningWidth);
    }

    // Convert the d3 node to a React node
    return svgWrapper.node().toReact();
  }
}

export default MultiLineGraph;



// WEBPACK FOOTER //
// ./src/components/MultiLineGraph/MultiLineGraph.js